import {
  Alert,
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { ResponsiveTable } from "@/components/Responsive";
import { MIN_TAP_TARGET, MOBILE_BREAKPOINT } from "@/styles/responsive";
import { type Field, fields, labels } from "./types";
import { validMapping } from "./rows";
import { type CorporationListState } from "./useCorporationList";
import { buttonSx, mock, Panel, PanelActions } from "./Panel";

interface Props {
  state: CorporationListState;
  disabled: boolean;
  onBack: () => void;
  onConfirm: () => void;
}

const PREVIEW_ROWS = 3;
// 番号がある行は番号だけで特定するため、所在地の列は番号がない行の照合にしか使わない
const addressFields: readonly Field[] = ["prefectureName", "cityName"];

/** ② 列のマッピング。見出しから判定した列を初期値にし、先頭3行のプレビューで確かめてから照合する */
export const MappingStep = ({
  state,
  disabled,
  onBack,
  onConfirm,
}: Props): JSX.Element => {
  const file = state.file;
  const valid = validMapping(state.mapping);
  return (
    <Panel
      title="列のマッピング"
      actions={
        <Typography sx={{ color: mock.muted, fontSize: 12 }}>
          {state.filename} / {(file?.rows.length ?? 0).toLocaleString()}行
        </Typography>
      }
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          gap: "15px 20px",
          maxWidth: 950,
          mx: "auto",
        }}
      >
        {fields.map((field) => {
          const id = `corporation-list-mapping-${field}`;
          return (
            <Box
              key={field}
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  [MOBILE_BREAKPOINT]: "180px 1fr",
                },
                alignItems: "center",
                gap: { xs: 0.5, [MOBILE_BREAKPOINT]: 0 },
                minHeight: 52,
              }}
            >
              <Box>
                <Typography
                  id={`${id}-label`}
                  sx={{ fontWeight: 700, fontSize: 14 }}
                >
                  {labels[field]}
                  {field === "companyName" && (
                    <Box
                      component="span"
                      sx={{ color: "#cf4c4c", fontSize: 11, ml: "4px" }}
                    >
                      必須*
                    </Box>
                  )}
                </Typography>
                {addressFields.includes(field) && (
                  <Typography sx={{ color: mock.muted, fontSize: 11 }}>
                    番号がない行で使用
                  </Typography>
                )}
              </Box>
              <TextField
                select
                size="small"
                id={id}
                required={field === "companyName"}
                disabled={disabled}
                value={state.mapping[field] ?? ""}
                // 見出しを左に置くため、TextField の label ではなく labelId で選択欄と結び付ける
                SelectProps={{ displayEmpty: true, labelId: `${id}-label` }}
                onChange={(event) => {
                  state.changeMapping({
                    ...state.mapping,
                    [field]:
                      event.target.value === ""
                        ? null
                        : Number(event.target.value),
                  });
                }}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "3px" },
                  // メニューを開くのは内側の.MuiSelect-select。外枠だけを高くすると押せる範囲は40pxのままになる。
                  // PCはモックの40px、SPはタップ領域の44px以上（レスポンシブ基盤ガイド）。
                  // MUIの「&.MuiSelect-select」のmin-heightより優先させるため、クラスを重ねて詳細度を上げる
                  "& .MuiSelect-select.MuiSelect-select": {
                    boxSizing: "border-box",
                    minHeight: { xs: MIN_TAP_TARGET, [MOBILE_BREAKPOINT]: 40 },
                    display: "flex",
                    alignItems: "center",
                    py: 0,
                  },
                }}
              >
                <MenuItem value="">割り当てない</MenuItem>
                {file?.columns.map((column) => (
                  <MenuItem key={column.index} value={column.index}>
                    {column.index + 1}列目: {column.header || "（列名なし）"}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          );
        })}
      </Box>
      <Box
        sx={{
          mt: "20px",
          px: "15px",
          py: "12px",
          bgcolor: "#fff9ee",
          color: "#80551e",
          border: "1px solid #eed5ad",
          fontSize: 12,
        }}
      >
        法人番号または会社法人等番号がない行は、法人名・都道府県・市区町村を使って候補を検索します。町名を指定すると同名法人を絞り込めます。
      </Box>
      {!valid && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          法人名の列を選び、同じ列を複数の項目に指定しないでください。
        </Alert>
      )}
      <Box sx={{ mt: "30px", pt: "23px", borderTop: `1px solid ${mock.line}` }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, mb: "11px" }}>
          ファイルプレビュー（先頭{PREVIEW_ROWS}行）
        </Typography>
        <ResponsiveTable
          title="ファイルプレビュー"
          headers={
            file?.columns.map(
              (column) => column.header || `${column.index + 1}列目`
            ) ?? []
          }
          rows={(file?.rows ?? [])
            .slice(0, PREVIEW_ROWS)
            .map((row) => row.cells.map((cell) => cell.value))}
        />
      </Box>
      <PanelActions>
        <Button
          variant="outlined"
          disabled={state.busy}
          onClick={onBack}
          sx={[buttonSx.base, buttonSx.secondary]}
        >
          戻る
        </Button>
        <Button
          variant="contained"
          disabled={disabled || !valid}
          onClick={onConfirm}
          sx={[buttonSx.base, buttonSx.primary]}
        >
          読み込んで法人を確認
        </Button>
      </PanelActions>
    </Panel>
  );
};
